import { useEffect, useRef } from 'react'
import { X, Sparkles, Tag } from 'lucide-react'
import changelog from '@changelog'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../stores/authStore'

const STORAGE_KEY = 'esi-whats-new-seen'

export function getSeenVersion(): string | null {
  return localStorage.getItem(STORAGE_KEY)
}

export function markVersionSeen(version: string) {
  localStorage.setItem(STORAGE_KEY, version)
}

export function hasUnseenUpdates(): boolean {
  return getSeenVersion() !== __APP_VERSION__
}

interface WhatsNewModalProps {
  open: boolean
  onClose: () => void
  maxReleases?: number
}

export default function WhatsNewModal({ open, onClose, maxReleases }: WhatsNewModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'owner' || user?.role === 'admin'

  useEffect(() => {
    const el = dialogRef.current
    if (!el) return
    if (open && !el.open) {
      el.showModal()
    } else if (!open && el.open) {
      el.close()
    }
  }, [open])

  const handleClose = () => {
    markVersionSeen(__APP_VERSION__)
    onClose()
  }

  const releases = (maxReleases ? changelog.releases.slice(0, maxReleases) : changelog.releases)
    .map((release) => ({
      ...release,
      entries: release.entries.filter((e) => {
        if (!e.audience.includes('portal')) return false
        if (e.role === 'admin' && !isAdmin) return false
        return true
      }),
    }))
    .filter((r) => r.entries.length > 0)

  if (!releases.length) return null

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      className="backdrop:bg-black/60 bg-transparent p-0 m-auto max-w-lg w-full"
    >
      <div className="bg-bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center">
              <Sparkles size={18} className="text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">What's New</h2>
              <p className="text-xs text-text-muted">v{__APP_VERSION__}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-5 max-h-[60vh] overflow-y-auto space-y-5">
          {releases.map((release) => (
            <div key={release.version}>
              <div className="flex items-center gap-2 mb-3">
                <Tag size={14} className="text-text-muted" />
                <span className="text-xs font-medium text-text-secondary">
                  v{release.version}
                </span>
                <span className="text-xs text-text-muted">
                  {new Date(release.date + 'T00:00:00').toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <div className="space-y-2.5">
                {release.entries.map((entry, i) => (
                  <div
                    key={i}
                    className={cn(
                      'rounded-lg border border-border p-3 bg-bg-primary/50',
                      entry.role === 'admin' && 'border-l-2 border-l-accent',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-text-primary">{entry.title}</h3>
                      {entry.role === 'admin' && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-accent/15 text-accent uppercase tracking-wider">
                          Admin
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-secondary mt-1 leading-relaxed">
                      {entry.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border bg-bg-primary/30">
          <button
            onClick={handleClose}
            className="w-full py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </dialog>
  )
}
